import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, InputAdornment, TextField, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';

export const AI_PROMPT_EXAMPLES = [
    { label: 'AiExampleBankFeesLabel', prompt: 'AiExampleBankFeesPrompt' },
    { label: 'AiExampleTransfersLabel', prompt: 'AiExampleTransfersPrompt' },
    { label: 'AiExampleReceiptsLabel', prompt: 'AiExampleReceiptsPrompt' },
    { label: 'AiExampleStandingOrdersLabel', prompt: 'AiExampleStandingOrdersPrompt' },
];

const primaryButtonSx = {
    height: 32,
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    px: 1.5,
    color: '#fff',
    background: 'linear-gradient(135deg, #304FFF 0%, #7B4DFF 100%)',
    '&:hover': { background: 'linear-gradient(135deg, #1E40FF 0%, #6A3BF0 100%)' },
};

const exampleChipSx = {
    height: 24,
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: 'var(--Foundation-Blue-Light, #EAEDFF)',
    color: 'var(--Foundation-Blue-Normal, #304FFF)',
    '&:hover': { backgroundColor: '#dbe0ff' },
};

/**
 * Free-text instruction for the AI matcher. "AI check" covers the whole period; "Suggest for
 * selection" limits the AI to the rows currently ticked in the two lists.
 */
const AiMatchingPromptBar = ({ onSuggest, loading = false, selectionCount = 0 }) => {
    const { t } = useTranslation();
    const [prompt, setPrompt] = useState('');

    const run = (useSelection, text = prompt) => {
        if (loading) return;
        onSuggest({ prompt: text.trim(), useSelection });
    };

    const handleExample = (example) => {
        const text = t(example.prompt);
        setPrompt(text);
        run(false, text);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: '1 1 420px', minWidth: 280 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            run(false);
                        }
                    }}
                    placeholder={t('AiPromptPlaceholder')}
                    disabled={loading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <AutoAwesomeIcon sx={{ fontSize: 18, color: '#7B4DFF' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: 34,
                            fontSize: '0.82rem',
                            borderRadius: '20px',
                            backgroundColor: 'var(--bank-surface-muted, #f7f8fc)',
                        },
                    }}
                />
                <Tooltip title={t('AiCheckHint')} arrow>
                    <span>
                        <Button
                            variant="contained"
                            onClick={() => run(false)}
                            disabled={loading}
                            sx={primaryButtonSx}
                            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                        >
                            {loading ? t('AiThinking') : t('AiCheck')}
                        </Button>
                    </span>
                </Tooltip>
                {selectionCount > 0 && (
                    <Tooltip title={t('AiSuggestForSelectionHint')} arrow>
                        <span>
                            <Button
                                variant="outlined"
                                onClick={() => run(true)}
                                disabled={loading}
                                sx={{
                                    height: 32,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    borderColor: '#7B4DFF',
                                    color: '#7B4DFF',
                                }}
                            >
                                {`${t('AiSuggestForSelection')} (${selectionCount})`}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {AI_PROMPT_EXAMPLES.map((example) => (
                    <Chip
                        key={example.label}
                        label={t(example.label)}
                        size="small"
                        clickable
                        disabled={loading}
                        onClick={() => handleExample(example)}
                        sx={exampleChipSx}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default AiMatchingPromptBar;
